import { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

// Fullscreen image lightbox with keyboard + arrow navigation.
export default function Lightbox({ images = [], index, onClose, onNavigate }) {
  const open = index !== null && index !== undefined && index >= 0;

  const prev = useCallback(() => onNavigate((index - 1 + images.length) % images.length), [index, images.length, onNavigate]);
  const next = useCallback(() => onNavigate((index + 1) % images.length), [index, images.length, onNavigate]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, prev, next]);

  const src = open ? (typeof images[index] === "string" ? images[index] : images[index].src) : null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="lightbox"
          className="fixed inset-0 z-[120] flex items-center justify-center bg-navy/95 backdrop-blur-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          onClick={onClose}
        >
          <button
            data-testid="lightbox-close"
            onClick={onClose}
            className="absolute right-5 top-5 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-ivory/25 text-ivory transition-colors hover:bg-ivory hover:text-navy md:right-8 md:top-8"
            aria-label="Close"
          >
            <X size={22} />
          </button>

          {images.length > 1 && (
            <>
              <button
                data-testid="lightbox-prev"
                onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-4 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-ivory/25 text-ivory transition-colors hover:bg-ivory hover:text-navy md:left-8"
                aria-label="Previous"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                data-testid="lightbox-next"
                onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-4 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-ivory/25 text-ivory transition-colors hover:bg-ivory hover:text-navy md:right-8 md:top-1/2"
                aria-label="Next"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}

          <motion.img
            key={src}
            src={src}
            alt="Vista Banquet Hall"
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="max-h-[86vh] max-w-[92vw] rounded-sm border border-gold/30 object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 overline text-ivory/60">
            {index + 1} / {images.length}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
